import {
  Button,
  Flex,
  Text,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
} from '@chakra-ui/react'
import { useRef, useState } from 'react'
import ConfirmButton from '../../Forms/components/ConfirmButton'
import { ISharedDiagnostic } from '../../../types/diagnostics'
import { useRespondToInvitationSharedDiagnostic } from '../../../api/api_hooks/invitations'
import { useTranslation } from 'react-i18next'

interface IReceiverButtonsProps {
  diagnostic: ISharedDiagnostic
  reloadReceivedSharedDiagnostics?: () => void
}

const ReceiverButtons = ({
  diagnostic,
  reloadReceivedSharedDiagnostics,
}: IReceiverButtonsProps) => {
  const { t } = useTranslation()
  const { mutateAsync: respondToCollaboration } =
    useRespondToInvitationSharedDiagnostic(diagnostic.id)

  const [pendingAction, setPendingAction] = useState<
    ISharedDiagnostic['receiverStatus'] | null
  >(null)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const confirmButtonColor = pendingAction === 'accepted' ? 'purple' : 'red'
  const acceptText =
    pendingAction === 'accepted'
      ? t('sharedDiagnostic.acceptConfirmMsg')
      : t('sharedDiagnostic.declineConfirmMsg')

  const respondToInvite = async (
    status: ISharedDiagnostic['receiverStatus']
  ) => {
    await respondToCollaboration(status)
    reloadReceivedSharedDiagnostics?.()
  }

  const handleConfirm = async () => {
    if (pendingAction) {
      await respondToInvite(pendingAction)
      setPendingAction(null)
      onClose()
    }
  }

  const openConfirmation = (action: ISharedDiagnostic['receiverStatus']) => {
    setPendingAction(action)
    onOpen()
  }

  const RespondButtons = () => {
    return (
      <Flex gap={2}>
        <Button
          variant="outline"
          border={'1px solid'}
          borderColor={'brand.lightGray'}
          color="brand.lightGrayText"
          w={'100%'}
          onClick={() => openConfirmation('declined')}
        >
          {t('shared.common.decline')}
        </Button>
        <ConfirmButton
          text={t('shared.common.accept')}
          w={'100%'}
          onClick={() => openConfirmation('accepted')}
        />
      </Flex>
    )
  }

  const ExpiredMessage = () => {
    return (
      <Flex gap={2}>
        <Text>{t('shared.common.expired')}</Text>
      </Flex>
    )
  }

  const DeclinedMessage = () => {
    return (
      <Flex gap={2}>
        <Text>{t('shared.common.declined')}</Text>
      </Flex>
    )
  }

  return (
    <>
      {diagnostic.receiverStatus === 'pending' && <RespondButtons />}
      {diagnostic.receiverStatus === 'expired' && <ExpiredMessage />}
      {diagnostic.receiverStatus === 'declined' && <DeclinedMessage />}

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          setPendingAction(null)
          onClose()
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('sharedDiagnostic.confirmInviteTitle')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {pendingAction === 'accepted'
                ? t('sharedDiagnostic.confirmAcceptInviteMsg')
                : t('sharedDiagnostic.confirmDeclineInviteMsg')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  setPendingAction(null)
                  onClose()
                }}
              >
                {t('shared.common.cancel')}
              </Button>
              <Button
                colorScheme={confirmButtonColor}
                onClick={handleConfirm}
                ml={3}
              >
                {acceptText}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}

export default ReceiverButtons
